import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useEntitlement } from '../context/EntitlementContext';

const TITLES = { login: 'Sign In', register: 'Create Account', reset: 'Reset Password' };

const AuthModal = ({ isOpen, onClose, initialMode = 'login' }) => {
  const { login, loginWithProvider, register, requestPasswordReset, error, clearError } = useAuth();
  const { billingEnabled } = useEntitlement();
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState('');
  const [notice, setNotice] = useState('');
  const { pathname } = useLocation();
  const openedOn = useRef(pathname);

  // Escape closes the dialog, and so does moving to another page.
  useEffect(() => {
    if (!isOpen) return undefined;
    openedOn.current = pathname;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose]);
  useEffect(() => {
    if (isOpen && pathname !== openedOn.current) onClose();
  }, [isOpen, pathname, onClose]);
  // Someone sent here to create an account starts on Register.
  useEffect(() => {
    if (isOpen) setMode(initialMode);
  }, [isOpen, initialMode]);

  if (!isOpen) return null;

  const switchMode = (newMode) => {
    setMode(newMode);
    setLocalError('');
    setNotice('');
    clearError();
  };

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    setNotice('');
    clearError();

    if (mode === 'register' && formData.password !== formData.confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }

    if (mode !== 'reset' && formData.password.length < 8) {
      setLocalError('Password must be at least 8 characters.');
      return;
    }

    setSubmitting(true);
    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        onClose();
      } else if (mode === 'reset') {
        await requestPasswordReset(formData.email);
        setNotice('If an account exists for that address, a reset link is on its way. Check your email.');
      } else {
        const { needsEmailConfirmation } = await register({
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          email: formData.email,
          password: formData.password,
        });
        if (needsEmailConfirmation) {
          setNotice('Almost done. Check your email and follow the confirmation link to finish creating your account.');
        } else {
          onClose();
        }
      }
    } catch {
      // error is already set in context
    } finally {
      setSubmitting(false);
    }
  };

  const handleProvider = async (provider) => {
    setLocalError('');
    try {
      await loginWithProvider(provider);
    } catch {
      // error is already set in context
    }
  };

  const displayError = localError || error;
  const submitLabel = { login: 'Sign In', register: 'Create Account', reset: 'Send Reset Link' }[mode];

  return (
    <div className="fixed inset-0 z-[100] overflow-y-auto" role="dialog" aria-modal="true" aria-label={TITLES[mode]}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative min-h-full flex items-center justify-center py-6">
        <div className="relative w-full max-w-md mx-4 terminal-card p-0 overflow-hidden">
          {/* Header */}
          <div className="bg-surface-elevated border-b border-surface-border px-6 py-4 flex items-center justify-between">
            <h2 className="font-display font-bold text-terminal-green crt-glow uppercase tracking-wider text-lg">
              {TITLES[mode]}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-txt-secondary hover:text-terminal-green transition-colors p-3 -m-3"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Mode Tabs */}
          {mode !== 'reset' && (
            <div className="flex border-b border-surface-border">
              {['login', 'register'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => switchMode(tab)}
                  className={`flex-1 py-3 text-xs font-mono uppercase tracking-wider transition-colors ${
                    mode === tab
                      ? 'text-terminal-green border-b-2 border-terminal-green bg-surface-elevated'
                      : 'text-txt-secondary hover:text-txt-primary'
                  }`}
                >
                  {tab === 'login' ? 'Sign In' : 'Register'}
                </button>
              ))}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {displayError && (
              <div className="bg-terminal-red/10 border border-terminal-red/30 rounded-lg px-4 py-3" role="alert">
                <p className="text-terminal-red text-sm font-mono">{displayError}</p>
              </div>
            )}
            {notice && (
              <div className="bg-terminal-dark-green/30 border border-terminal-dim-green rounded-lg px-4 py-3" role="status">
                <p className="text-terminal-green text-sm font-mono">{notice}</p>
              </div>
            )}

            {mode !== 'reset' && (
              <>
                <button
                    type="button"
                    onClick={() => handleProvider('apple')}
                    className="w-full flex items-center justify-center gap-3 bg-white text-black py-3 rounded-lg font-sans font-medium text-sm"
                  >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M16.37 1.43c0 1.14-.47 2.22-1.2 3.02-.8.87-2.1 1.54-3.16 1.46-.14-1.1.42-2.27 1.14-3.02.8-.85 2.19-1.48 3.22-1.46zM20.5 17.1c-.55 1.27-.82 1.84-1.53 2.96-.99 1.57-2.39 3.52-4.12 3.53-1.54.02-1.94-1-4.03-.99-2.09.01-2.53 1.01-4.07.99-1.73-.02-3.06-1.78-4.05-3.35C-.06 15.88-.35 10.7 1.4 7.98c1.24-1.93 3.2-3.06 5.04-3.06 1.87 0 3.05 1.03 4.6 1.03 1.5 0 2.42-1.03 4.58-1.03 1.64 0 3.37.9 4.61 2.44-4.05 2.22-3.4 8.01.27 9.74z" />
                    </svg>
                    Sign in with Apple
                  </button>
                <button
                  type="button"
                  onClick={() => handleProvider('google')}
                  className="w-full flex items-center justify-center gap-3 bg-surface-elevated border border-surface-border hover:border-terminal-dim-green py-3 rounded-lg font-mono text-sm text-txt-primary transition-colors"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                  </svg>
                  Continue with Google
                </button>

                <div className="flex items-center gap-3">
                  <div className="flex-1 border-t border-surface-border"></div>
                  <span className="text-xs text-txt-secondary font-mono uppercase">or</span>
                  <div className="flex-1 border-t border-surface-border"></div>
                </div>
              </>
            )}

            {mode === 'reset' && (
              <p className="text-sm text-txt-secondary">
                Enter your account email and we'll send you a link to set a new password.
              </p>
            )}

            {mode === 'register' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="auth-firstName" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">
                    First Name
                  </label>
                  <input
                    id="auth-firstName"
                    name="firstName"
                    type="text"
                    value={formData.firstName}
                    onChange={handleChange}
                    className="terminal-input w-full"
                    placeholder="Optional"
                    autoComplete="given-name"
                  />
                </div>
                <div>
                  <label htmlFor="auth-lastName" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">
                    Last Name
                  </label>
                  <input
                    id="auth-lastName"
                    name="lastName"
                    type="text"
                    value={formData.lastName}
                    onChange={handleChange}
                    className="terminal-input w-full"
                    placeholder="Optional"
                    autoComplete="family-name"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="auth-email" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">
                Email
              </label>
              <input
                id="auth-email"
                name="email"
                type="email"
                inputMode="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="terminal-input w-full"
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            {mode !== 'reset' && (
              <div>
                <label htmlFor="auth-password" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">
                  Password
                </label>
                <input
                  id="auth-password"
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  className="terminal-input w-full"
                  placeholder="Min 8 characters"
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                />
                {mode === 'login' && (
                  <button
                    type="button"
                    onClick={() => switchMode('reset')}
                    className="mt-2 text-xs font-mono text-terminal-cyan hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
            )}

            {mode === 'register' && (
              <div>
                <label htmlFor="auth-confirmPassword" className="block text-txt-secondary text-xs font-mono uppercase tracking-wide mb-1">
                  Confirm Password
                </label>
                <input
                  id="auth-confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="terminal-input w-full"
                  placeholder="Re-enter password"
                  autoComplete="new-password"
                />
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full glow-btn glow-btn-green py-3 rounded-lg font-mono uppercase tracking-wider text-sm disabled:opacity-40 transition-all"
            >
              {submitting ? 'Processing...' : submitLabel}
            </button>

            {mode !== 'reset' && (
              <p className="text-center text-xs text-txt-secondary">
                By continuing you agree to the{' '}
                <Link to="/terms" onClick={onClose} className="text-terminal-cyan hover:underline">Terms of Service</Link>
                {' '}and{' '}
                <Link to="/privacy" onClick={onClose} className="text-terminal-cyan hover:underline">Privacy Policy</Link>.
              </p>
            )}

            <p className="text-center text-xs text-txt-secondary font-mono">
              {mode === 'login' && (
                <>No account? <button type="button" onClick={() => switchMode('register')} className="text-terminal-cyan hover:underline">Register</button></>
              )}
              {mode === 'register' && (
                <>Already have an account? <button type="button" onClick={() => switchMode('login')} className="text-terminal-cyan hover:underline">Sign In</button></>
              )}
              {mode === 'reset' && (
                <button type="button" onClick={() => switchMode('login')} className="text-terminal-cyan hover:underline">Back to sign in</button>
              )}
            </p>
          </form>

          {/* Guest hint */}
          <div className="border-t border-surface-border px-6 py-3 bg-surface-elevated space-y-2">
            <p className="text-xs text-txt-secondary font-mono text-center">
              {billingEnabled
                ? 'New here? Register, or continue with Apple or Google. Syncing your plan across devices needs Pro. Everything else works the same signed in or not.'
                : 'New here? Register, or continue with Apple or Google. An account keeps your plan in sync across your devices. Where Pro is on sale, sync is part of it.'}
            </p>
            <p className="text-xs text-txt-secondary font-mono text-center">
              Or continue as a guest. Your plan stays on this device and isn't sent to us.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;
